import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import TextAlign from "@tiptap/extension-text-align";
import { useEffect } from "react";
import {
  Bold, Italic, Underline as UIcon, List, ListOrdered,
  Link as LinkIcon, Quote, Heading1, Heading2, AlignLeft, AlignCenter, AlignRight,
} from "lucide-react";

const btnCls = (active) =>
  `p-1.5 border ${active ? "border-cyan-400 bg-[#0F0F0F] text-cyan-400" : "border-[#27272A] text-[#A1A1AA] hover:text-white"}`;

export default function RichTextEditor({ value, onChange, testid = "rte" }) {
  const editor = useEditor({
    extensions: [
      // StarterKit ya incluye Underline en 3.x; no lo importamos por separado
      // para evitar el warning "Duplicate extension names".
      StarterKit.configure({ link: false }),
      Link.configure({ openOnClick: false, autolink: false, HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" } }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => onChange?.(editor.getHTML()),
    editorProps: {
      attributes: { class: "prose-tactical min-h-[220px] p-4 focus:outline-none" },
    },
  });

  // Sync external `value` cambios (ej: cargar post existente).
  useEffect(() => {
    if (!editor) return;
    if (value !== undefined && value !== editor.getHTML()) {
      editor.commands.setContent(value || "", false);
    }
  }, [value, editor]);

  if (!editor) return null;

  const setLink = () => {
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("URL (http/https):", prev);
    if (url === null) return;
    if (url === "") { editor.chain().focus().unsetLink().run(); return; }
    if (!/^https?:\/\//i.test(url)) { window.alert("URL debe empezar con http:// o https://"); return; }
    editor.chain().focus().setLink({ href: url }).run();
  };

  return (
    <div className="border border-[#27272A] bg-[#0A0A0A]" data-testid={testid}>
      <div className="flex flex-wrap gap-1 p-2 border-b border-[#27272A] bg-[#111]">
        <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} className={btnCls(editor.isActive("heading", { level: 1 }))} title="Título 1"><Heading1 size={14}/></button>
        <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} className={btnCls(editor.isActive("heading", { level: 2 }))} title="Título 2"><Heading2 size={14}/></button>
        <div className="w-px h-6 bg-[#27272A] mx-1"/>
        <button type="button" onClick={() => editor.chain().focus().toggleBold().run()} className={btnCls(editor.isActive("bold"))} title="Negrita"><Bold size={14}/></button>
        <button type="button" onClick={() => editor.chain().focus().toggleItalic().run()} className={btnCls(editor.isActive("italic"))} title="Cursiva"><Italic size={14}/></button>
        <button type="button" onClick={() => editor.chain().focus().toggleUnderline().run()} className={btnCls(editor.isActive("underline"))} title="Subrayado"><UIcon size={14}/></button>
        <div className="w-px h-6 bg-[#27272A] mx-1"/>
        <button type="button" onClick={() => editor.chain().focus().toggleBulletList().run()} className={btnCls(editor.isActive("bulletList"))} title="Lista"><List size={14}/></button>
        <button type="button" onClick={() => editor.chain().focus().toggleOrderedList().run()} className={btnCls(editor.isActive("orderedList"))} title="Lista numerada"><ListOrdered size={14}/></button>
        <button type="button" onClick={() => editor.chain().focus().toggleBlockquote().run()} className={btnCls(editor.isActive("blockquote"))} title="Cita"><Quote size={14}/></button>
        <div className="w-px h-6 bg-[#27272A] mx-1"/>
        <button type="button" onClick={() => editor.chain().focus().setTextAlign("left").run()} className={btnCls(editor.isActive({ textAlign: "left" }))} title="Alinear izquierda"><AlignLeft size={14}/></button>
        <button type="button" onClick={() => editor.chain().focus().setTextAlign("center").run()} className={btnCls(editor.isActive({ textAlign: "center" }))} title="Centrar"><AlignCenter size={14}/></button>
        <button type="button" onClick={() => editor.chain().focus().setTextAlign("right").run()} className={btnCls(editor.isActive({ textAlign: "right" }))} title="Alinear derecha"><AlignRight size={14}/></button>
        <div className="w-px h-6 bg-[#27272A] mx-1"/>
        <button type="button" onClick={setLink} className={btnCls(editor.isActive("link"))} title="Enlace"><LinkIcon size={14}/></button>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
